/*
 * Project: bellcloud
 * File: packages/ui/components/common/Button.tsx
 * Description: Reusable styled button components
 */

import styled from '@emotion/styled';
import { css } from '@emotion/react';
import type { SpacingTokens } from '@/theme/tokens/spacing';

type ButtonType = 'primary' | 'secondary' | 'tertiary' | 'tertiaryNeutral';

export const Button = styled.button<{
  variant?: ButtonType;
  width?: string;
  /** full row width (e.g. login submit) */
  fullWidth?: boolean;
}>`
  display: flex;
  width: ${({ width, fullWidth }) => (fullWidth ? '100%' : width || '164px')};
  padding: ${({ theme }) => `${theme.spacing[2]}px ${theme.spacing[6]}px`};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: ${({ theme }) => `${theme.borderRadius.lg}px`};
  border: none;

  font-size: ${({ theme }) => `${theme.fontSize.md}px`};
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  line-height: ${({ theme }) => theme.lineHeight.normal};
  text-align: center;

  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  ${({ variant = 'primary', theme }) => {
    switch (variant) {
      case 'primary':
        return css`
          background-color: ${theme.colors.button.bg.primary.default};
          color: ${theme.colors.button.text.primary};

          &:hover:not(:disabled) {
            background-color: ${theme.colors.button.bg.primary.hover};
          }
        `;
      case 'secondary':
        return css`
          background-color: ${theme.colors.button.bg.secondary.default};
          color: ${theme.colors.button.text.secondary};

          &:hover:not(:disabled) {
            background-color: ${theme.colors.button.bg.secondary.hover};
          }
        `;
      case 'tertiary':
        return css`
          background-color: transparent;
          color: ${theme.colors.button.text.tertiaryBrand};
          border: 1px solid ${theme.colors.button.stroke.tertiaryBrand.default};

          &:hover:not(:disabled) {
            border-color: ${theme.colors.button.stroke.tertiaryBrand.hover};
          }
        `;
      case 'tertiaryNeutral':
        return css`
          background-color: transparent;
          color: ${theme.colors.button.text.tertiaryNeutral};
          border: 1px solid ${theme.colors.button.stroke.tertiaryNeutral.default};

          &:hover:not(:disabled) {
            border-color: ${theme.colors.button.stroke.tertiaryNeutral.hover};
            background-color: rgba(29, 29, 29, 0.05);
          }

          &:active:not(:disabled) {
            background-color: rgba(29, 29, 29, 0.1);
          }
        `;
      default:
        return css`
          background-color: ${theme.colors.button.bg.primary.default};
          color: ${theme.colors.button.text.primary};
        `;
    }
  }}

  &:disabled {
    background-color: ${({ theme }) => theme.colors.button.bg.disabled};
    color: ${({ theme }) => theme.colors.button.text.disabled};
    border: none;
    cursor: not-allowed;
  }

  &:focus-visible:not(:disabled) {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.button.bg.primary.default}30;
  }
`;

export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => `${theme.spacing[2]}px`};
  background: transparent;
  border: none;
  border-radius: ${({ theme }) => `${theme.borderRadius.md}px`};
  color: ${({ theme }) => theme.colors.icon.secondary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.surface.interactive.hover};
    color: ${({ theme }) => theme.colors.icon.primary};
  }

  &:disabled {
    color: ${({ theme }) => theme.colors.icon.disabled};
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.border.group.brand}30;
  }
`;

export const TextButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding: ${({ theme }) => `${theme.spacing[2]}px ${theme.spacing[4]}px`};
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => `${theme.fontSize.md}px`};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  text-decoration: underline;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.button.bg.primary.default};
  }

  &:disabled {
    color: ${({ theme }) => theme.colors.text.disabled};
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.button.bg.primary.default}30;
  }
`;

export const ButtonGroup = styled.div<{ gap?: keyof SpacingTokens }>`
  display: flex;
  gap: ${({ theme, gap }) => (gap ? `${theme.spacing[gap]}px` : `${theme.spacing[4]}px`)};
  align-items: center;
`;
